import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {scaleNumber} from "../../helpers/scaling";
import {getGraphColor} from "../../helpers/dark_mode";
import {safeBigIntToNumber} from "../../helpers/number_coerce";

export class ShaperLiveBits extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.seriesShapedDown = [];
        this.seriesShapedUp = [];
        this.maxPoints = 120; // ~2 minutes of history
    }

    title() { return "Live Bits/s"; }
    supportsZoom() { return false; }
    tooltip() { return "<h5>Live Throughput</h5><p>Current shaped download/upload. Upload inverted.</p>"; }

    buildContainer() {
        let base = super.buildContainer();
        // Apply a border by default; color updated on shaper_detail
        base.classList.add('border');
        // Status line shown until first live sample arrives
        const status = document.createElement('div');
        status.id = this.id + '_status';
        status.classList.add('text-muted');
        status.style.fontSize = '0.85rem';
        status.style.marginTop = '4px';
        status.innerText = 'Connecting (requires LibreQoS V1.5 RC2 or greater)';
        base.appendChild(status);
        const g = this.graphDiv();
        g.style.height = "150px";
        base.appendChild(g);
        return base;
    }

    setup() {
        super.setup();
        this.graph = new LiveBitsGraph(this.graphDivId());
        this.graph.update([], []);
    }

    channelRequests() { return ["ShaperDetail", "ShaperLiveThroughput"]; }

    onMessage(msg) {
        if (msg.Data.target === "shaper_detail") {
            // Update border color based on last_seen like all_shapers
            const entries = Object.entries(msg.Data.blob);
            if (entries.length > 0) {
                const [_, value] = entries[0];
                const nowUTC = value.now_server;
                const lastSeen = value.header.last_seen;
                const timeDiff = nowUTC - lastSeen;
                const container = document.getElementById(this.id);
                container.classList.remove('border-success','border-warning','border-danger');
                if (timeDiff < 300) container.classList.add('border-success');
                else if (timeDiff < 600) container.classList.add('border-warning');
                else container.classList.add('border-danger');
            }
            return;
        }
        if (msg.Data.target !== "shaper_live_throughput") return;
        const p = msg.Data.blob; // live payload { shaped_bytes_down, shaped_bytes_up, ... }

        // Maintain shaped down (positive) and shaped up (negative) series
        const sDown = safeBigIntToNumber(p.shaped_bytes_down || 0);
        const sUp = -safeBigIntToNumber(p.shaped_bytes_up || 0);
        this.seriesShapedDown.push(sDown);
        this.seriesShapedUp.push(sUp);
        if (this.seriesShapedDown.length > this.maxPoints) this.seriesShapedDown.shift();
        if (this.seriesShapedUp.length > this.maxPoints) this.seriesShapedUp.shift();

        // Hide status once we have a sample
        const status = document.getElementById(this.id + '_status');
        if (status) status.style.display = 'none';
        this.graph.update(this.seriesShapedDown, this.seriesShapedUp);
    }
}

class LiveBitsGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(60)
            .withSequenceAxis(0, 0)
            .withScaledAbsYAxis("Bandwidth", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        // Use two decimal places on Y-axis labels to preserve precision (e.g., 1.25G)
        if (this.option.yAxis && this.option.yAxis.axisLabel) {
            this.option.yAxis.axisLabel.formatter = (val) => scaleNumber(Math.abs(val), 1);
        }
        // Tighten grid and hide legend/axis labels for sparkline look
        this.option.grid = { left: 55, right: 12, top: 10, bottom: 10 };
        this.option.legend = { show: false };
        this.option.xAxis.axisLabel = { show: false };
        this.option && this.chart.setOption(this.option);
    }

    update(shapedDown, shapedUp) {
        this.chart.hideLoading();
        const len = Math.max(shapedDown.length, shapedUp.length);
        const x = Array.from({length: len}, (_, i) => i);
        this.option.xAxis.data = x;
        this.option.series = [];
        this.option.legend.data = [];

        const mk = (name, data, color, stack) => ({
            type: 'line',
            data,
            name,
            smooth: true,
            showSymbol: false,
            lineStyle: { width: 1 },
            itemStyle: { color },
            areaStyle: { color, opacity: 0.2 },
            stack
        });

        // Two series: Shaped Down (positive), Shaped Up (negative)
        const liveColor = getGraphColor(2);
        const sDown = mk('Shaped Down', shapedDown, liveColor, 'down');
        const sUp = mk('Shaped Up', shapedUp, liveColor, 'up');
        this.option.series.push(sDown);
        this.option.series.push(sUp);
        // Let echarts auto-range to include both positive and negative
        if (this.option.yAxis && this.option.yAxis.min !== undefined) delete this.option.yAxis.min;

        this.option.tooltip = {
            trigger: 'axis',
            axisPointer: { type: 'cross', label: { backgroundColor: 'rgba(30,30,30,0.92)', color: '#fff', fontFamily: 'inherit', formatter: function(p){ return scaleNumber(Math.abs(p.value), 2) + 'bps'; } } },
            backgroundColor: 'rgba(30,30,30,0.92)', textStyle: { fontSize: 10, color: '#fff', fontFamily: 'inherit' },
            formatter: (params) => {
                const idx = params[0].dataIndex;
                const downVal = shapedDown[idx] || 0;
                const upVal = Math.abs(shapedUp[idx] || 0);
                return `<div style=\"font-size:10px;\"><strong>Shaped Throughput</strong><br/>Down: ${scaleNumber(downVal,2)}bps<br/>Up: ${scaleNumber(upVal,2)}bps</div>`;
            }
        };

        this.chart.setOption(this.option);
    }
}
